"use client"

import { getSiteUrl } from "@repo/shared"
import IconFont from "@repo/ui/iconfont"
import { deleteCookie } from "cookies-next"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { useEffect, useState } from "react"
import { Avatar, Popover, Space } from "antd"

import { useUserInfo } from "../UserProvider"

import "./index.scss"

/**
 * 截断文本到指定长度，超出部分显示省略号
 * @param text 要截断的文本
 * @param maxLength 最大长度
 * @returns 截断后的文本
 */
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return ""
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + "..."
}

/**
 * 用户信息组件
 * 显示用户头像、名称，支持下拉菜单
 */
export function UserProfile() {
  const { userInfo } = useUserInfo()
  const locale = useLocale()
  const t = useTranslations("HeaderComp")

  // 解决 SSR 渲染 Popover 导致的 Hydration Mismatch 问题
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const dropdownItems = [
    {
      label: t("utilityNav.Account"),
      key: "account",
      href: getSiteUrl("user") + "/" + locale + "/account",
    },
    {
      label: t("utilityNav.MyWatchlist"),
      key: "watchlist",
      href: getSiteUrl("www") + "/" + locale + "/watchlist",
    },
    {
      label: t("utilityNav.SignOut"),
      key: "signout",
    },
  ]

  const handleDropdownItemClick = (key: string) => {
    if (key === "signout") {
      deleteCookie(process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "", {
        domain: ".metal.com",
        path: "/",
      })
      // 使用 window.location.reload() 刷新页面，因为 App Router 的 router 没有 reload 方法
      window.location.reload()
    }
  }

  const userContent = (
    <div className="userInfo">
      <Space size={8} align="center">
        <Avatar
          size={24}
          src={userInfo?.avatar || `https://imgqn.smm.cn/static/image/metal/avatar.svg`}
          className="userAvatar"
        />
        <div className="userName">
          <p className="userNameText" title={userInfo?.name || "-"}>
            {truncateText(userInfo?.name || "-", 20)}
          </p>
          <IconFont
            type="icon-a-Down-cxia-yuan1"
            style={{
              fontSize: 16,
            }}
          />
        </div>
      </Space>
    </div>
  )

  if (!mounted) {
    return userContent
  }

  return (
    <Popover
      placement="topLeft"
      title={null}
      trigger={["hover"]}
      classNames={{
        root: "userDropdownOverlayGlobal",
      }}
      styles={{
        body: {
          padding: 0,
        },
      }}
      content={
        <div className="userDropdownOverlay">
          {dropdownItems.map((item) => {
            if (item.href) {
              return (
                <Link href={item.href} key={item.key} className="userDropdownItem">
                  <div className="userDropdownContent">{item.label}</div>
                </Link>
              )
            } else {
              return (
                <div key={item.key} className="userDropdownItem" onClick={() => handleDropdownItemClick(item.key)}>
                  <div className="userDropdownContent">{item.label}</div>
                </div>
              )
            }
          })}
        </div>
      }
      arrow={false}
    >
      {userContent}
    </Popover>
  )
}
